import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import './front.css';
import { ANELLI } from './data/catena';

/**
 * FRONT END 1 — pagina di atterraggio dopo il login (rotta /servizi).
 *
 * Vi accede chi entra con un ruolo operativo (Funzionario AdG, Auditor,
 * Beneficiario). Il Platform Admin viene invece indirizzato a /console.
 *
 * Ha due compiti, in quest'ordine:
 *  1. far capire COME lavora la piattaforma (la catena dei cinque passaggi):
 *     è un'amministrazione pubblica, deve poter spiegare a un auditor perché
 *     un atto assistito è sostenibile;
 *  2. portare al lavoro vero, cioè alla pagina «Da dove partiamo» (/immersivo).
 *
 * Ogni percorso che porta a scrivere un atto — il pulsante dell'intestazione,
 * la pill «Ambiente immersivo», i «Riprendi» sulle card, la card di servizio
 * «Redazione di avvisi e bandi», l'azione dell'anello 04 — punta a /immersivo.
 * Non lasciare ancore #immersivo: la pagina esiste, i fragment sono link morti.
 *
 * `/dashboard` (addendum "Front end: pagina dei servizi e ambiente
 * immersivo" §2) resta la seconda destinazione più probabile dopo
 * l'ambiente immersivo: la pill «Il mio lavoro» e il link della sezione
 * «I tuoi atti» ci puntano con la rotta reale, non un segnaposto. I widget
 * operativi (onboarding, checklist RI, conformità) restano solo lì: non si
 * duplicano qui.
 */
export function ServiziPage() {
  const [anello, setAnello] = useState(ANELLI[0]!.id);
  const attivo = ANELLI.find((a) => a.id === anello) ?? ANELLI[0]!;

  return (
    <div className="uf">
      <div className="uf-striscia">
        <span>Autorità di Gestione · politiche di coesione UE</span>
        <span>Programmazione 2021–2027 · UranIA PA</span>
      </div>

      <header className="uf-hdr">
        <div className="uf-marchio">
          <div className="uf-globo" aria-hidden="true"><span /><span /></div>
          <span className="uf-wordmark">Uran<i>IA</i> <small>PA</small></span>
        </div>
        <input className="uf-cerca" type="search" placeholder="Cerca un atto, una norma, un servizio…" />
        <div className="uf-utente">
          <a href="/assistenza">Assistenza</a>
          <span className="uf-avatar" aria-hidden="true">CL</span>
          <span className="uf-chi">
            <strong>c.lentini</strong>
            <span>Funzionario AdG · L2</span>
          </span>
        </div>
      </header>

      <div className="uf-colonna">
        <section className="uf-saluto">
          <div>
            <div className="uf-data">Martedì 8 settembre 2026</div>
            <h1>Buongiorno, Claudia</h1>
            {/* Dice che cosa aspetta l'operatore, non "benvenuta". */}
            <p>Hai <strong>2 atti in redazione</strong> e <strong>1 rilievo di audit</strong> in attesa di risposta entro venerdì.</p>
          </div>
          <div className="uf-saluto-azioni">
            <Link to="/immersivo" className="uf-btn chiaro">Riprendi la redazione</Link>
            <Link to="/immersivo" className="uf-btn fantasma">Nuovo atto — Da dove partiamo</Link>
          </div>
        </section>

        {/* ---------- la catena ---------- */}
        <section className="uf-card">
          <div className="uf-card-hdr">
            <div>
              <h2>Come lavora UranIA</h2>
              <p>Non un assistente conversazionale, ma una catena di cinque passaggi. Selezionane uno per vedere che cosa fa e che cosa garantisce.</p>
            </div>
            <span className="uf-tag-mono">tutto on-premise</span>
          </div>

          <div className="uf-catena">
            {ANELLI.map((a) => (
              <button
                key={a.id}
                type="button"
                className={a.id === attivo.id ? 'uf-anello sel' : 'uf-anello'}
                aria-pressed={a.id === attivo.id}
                onClick={() => setAnello(a.id)}
              >
                <span className="uf-anello-testa">
                  <span className="uf-anello-num">{a.numero}</span>
                  <span className="uf-anello-nome">{a.nome}</span>
                </span>
                <span className="uf-anello-sub">{a.sottotitolo}</span>
                <span className="uf-anello-cifra">{a.cifra}</span>
              </button>
            ))}
          </div>

          <div className="uf-dettaglio">
            <div className="uf-dettaglio-testo">
              <div className="uf-dettaglio-num">ANELLO {attivo.numero} DI {ANELLI.length}</div>
              <h3>{attivo.nome}</h3>
              <p className="uf-claim">{attivo.claim}</p>
              <ul className="uf-punti">
                {attivo.punti.map((p) => <li key={p.testo}>{p.testo}</li>)}
              </ul>
              <div className="uf-garanzia">
                <div className="uf-etichetta">LA GARANZIA</div>
                <p>{attivo.garanzia}</p>
              </div>
            </div>
            <div className="uf-dettaglio-metriche">
              {attivo.metriche.map((m) => (
                <div className="uf-metrica" key={m.etichetta}>
                  <div className="uf-metrica-et">{m.etichetta}</div>
                  <div className="uf-metrica-val">{m.valore}</div>
                </div>
              ))}
              {/* I link degli anelli sono rotte reali dove esistono. */}
              {attivo.link.startsWith('/')
                ? <Link to={attivo.link} className="uf-btn chiaro pieno">{attivo.azione}</Link>
                : <a href={attivo.link} className="uf-btn chiaro pieno">{attivo.azione}</a>}
            </div>
          </div>

          <p className="uf-inverso">
            La catena si percorre anche in senso inverso: davanti a un atto firmato si risale all'agente che ha
            formulato la proposta, alle fonti recuperate, alla versione dell'archivio e alla persona che ha
            validato. È ciò che rende un atto assistito sostenibile in sede di audit.
          </p>

          {/* Il ponte verso la pagina successiva: senza questo la home resta
              una brochure. */}
          <div className="uf-ponte">
            <div>
              <div className="uf-ponte-titolo">La catena entra in funzione quando apri un atto</div>
              <p>
                Il passaggio successivo è la pagina <strong>«Da dove partiamo»</strong>: si sceglie l'ente, la
                tipologia di atto e il format — oppure si carica un documento già iniziato. Da quel momento
                guida, agenti e archivio lavorano su quell'atto.
              </p>
            </div>
            <Link to="/immersivo" className="uf-btn indigo">Vai a «Da dove partiamo»</Link>
          </div>
        </section>

        {/* ---------- strumenti, atti, servizi ----------
            Sono elenchi editoriali: tenerli in JSX è voluto, così il contenuto
            resta modificabile senza toccare la logica. */}
        <section className="uf-card">
          <div className="uf-card-hdr">
            <div><h2>I tuoi strumenti</h2><p>Quelli che usi di più, sempre a portata di mano.</p></div>
            <a href="/preferenze">Personalizza</a>
          </div>
          <div className="uf-pills">
            <Link to="/dashboard" className="uf-pill">Il mio lavoro</Link>
            <Link to="/immersivo" className="uf-pill primaria">Ambiente immersivo</Link>
            <a href="/servizi/conformita" className="uf-pill viola">Verifica di conformità</a>
            <a href="/servizi/ricerca" className="uf-pill viola">Ricerca normativa</a>
            <a href="/servizi/cowork" className="uf-pill">Cowork</a>
            <a href="/servizi/rendicontazione" className="uf-pill">Rendicontazione</a>
            <a href="/servizi/archivio" className="uf-pill">Archivio atti</a>
          </div>
        </section>

        <section className="uf-card">
          <div className="uf-card-hdr">
            <div><h2>I tuoi atti</h2><p>Nessuna bozza è protocollabile prima della validazione umana.</p></div>
            <Link to="/dashboard">Vedi tutti nel mio lavoro</Link>
          </div>
          <div className="uf-atti">
            <article className="uf-atto lavanda">
              <header><span className="uf-kicker">AVVISO PUBBLICO</span><span className="uf-stato ambra">In redazione</span></header>
              <h3>Efficienza energetica PMI</h3>
              <p>FESR · versione archivio v12 · con g.rizzo</p>
              <div className="uf-avanzamento"><div style={{ width: '72%' }} /></div>
              <footer><span>72% · toccato 2 ore fa</span><Link to="/immersivo">Riprendi</Link></footer>
            </article>
            <article className="uf-atto azzurra">
              <header><span className="uf-kicker">DECRETO</span><span className="uf-stato rossa">Manca base giuridica</span></header>
              <h3>Decreto 412/2026 — ammissione</h3>
              <p>FSE+ · versione archivio v8 · 6 passaggi assistiti</p>
              <div className="uf-avanzamento"><div style={{ width: '34%' }} /></div>
              <footer><span>34% · revisore da assegnare</span><Link to="/immersivo">Riprendi</Link></footer>
            </article>
            <article className="uf-atto verde">
              <header><span className="uf-kicker">DETERMINA</span><span className="uf-stato verde">Validata</span></header>
              <h3>Determina 407/2026 — impegno</h3>
              <p>FESR · validata da g.rizzo</p>
              <div className="uf-avanzamento"><div style={{ width: '100%' }} /></div>
              <footer><span>Pronta per il protocollo</span><a href="/servizi/fascicolo/407">Fascicolo</a></footer>
            </article>
            <Link to="/immersivo" className="uf-atto nuovo">
              <span className="uf-piu">+</span>
              <span className="uf-nuovo-t">Nuovo atto</span>
              <span className="uf-nuovo-n">da format o da un documento,<br />con l'assistente al fianco</span>
            </Link>
          </div>
        </section>

        <div className="uf-aiact">
          <span className="tag">AI ACT</span>
          <p>
            I servizi contrassegnati come <strong>assistiti</strong> impiegano modelli linguistici ai sensi del
            Reg. UE 2024/1689 (art. 50) e della L. 132/2025. Le loro risposte sono proposte: verifica sempre la
            base giuridica e le fonti citate prima di assumere decisioni amministrative.{' '}
            <a href="/normativa">Normativa</a>
          </p>
        </div>
      </div>
    </div>
  );
}
